import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

import { traineesActions } from "@fitness-app/app-store";

import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

export interface TraineeSearchOption {
  value: string;
  // Dropdown row — the email disambiguates two clients with the same name.
  label: string;
  // What the closed select shows. The drawer renders inside a bordered
  // Descriptions, whose table is table-layout:auto, so a selected "Name (email)"
  // widens the column past the drawer instead of ellipsizing (TRE-2005).
  shortLabel: string;
  metadata?: { email?: string };
}

// Debounced client lookup behind the two places support tickets name a client:
// the new-request modal and the drawer's "Klient" field. `enabled` gates the
// fetching so a closed modal / drawer doesn't hold the shared trainees search
// term — it is global store state, and the trainees module reads it too.
export const useTraineeSearch = ({
  enabled,
  extraOption,
}: {
  enabled: boolean;
  // Kept visible even when it is not in the current results — the already-linked
  // client, or one prefilled from chat, is rarely in an empty search's first page.
  extraOption?: (Omit<TraineeSearchOption, "shortLabel"> & { shortLabel?: string }) | null;
}) => {
  const dispatch = useAppDispatch();
  const trainees = useAppSelector((state) => state.trainees.list);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, { isPending }] = useDebounce(searchValue, 300);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    dispatch(traineesActions.setSearchTerm(debouncedSearchValue));
    void dispatch(traineesActions.fetchTrainees());

    return () => {
      dispatch(traineesActions.setSearchTerm(""));
    };
  }, [debouncedSearchValue, dispatch, enabled]);

  const options: TraineeSearchOption[] = [
    ...(extraOption && !trainees.some((trainee) => trainee.id === extraOption.value)
      ? [{ ...extraOption, shortLabel: extraOption.shortLabel ?? extraOption.label }]
      : []),
    ...trainees.map((trainee) => {
      const name = [trainee.firstName, trainee.lastName].filter(Boolean).join(" ") || trainee.email;
      return {
        value: trainee.id,
        metadata: { email: trainee.email },
        label: `${name} (${trainee.email})`,
        shortLabel: name,
      };
    }),
  ];

  return { options, isSearching: isPending(), setSearchValue };
};
